// Form design tokens — the SINGLE source of truth for the four optional
// appearance controls (font family · size · button radius · density).
//
// Pure, framework-agnostic, ZERO runtime deps — safe to import in the
// embed bundle (no pg / redis / react), exactly like form-render-core.ts.
// All three renderers (embed/index.ts, RequestPageClient.tsx,
// FormPreview.tsx) resolve their appearance through resolveFormDesignTokens()
// so the in-app preview can never drift from production.
//
// Byte-identical contract: an untouched store (no saved overrides) renders
// byte-for-byte as before. Legacy enum strings stored by Phase-2c are
// back-compat-mapped to their numeric equivalents in the resolver.
//
// Sliders (Phase polish-v2): form_size, form_button_radius, form_density are
// now stored as numbers (scale / px / multiplier). The font family stays a
// discrete string option (dropdown). Back-compat: legacy enum strings ("small",
// "rounded", "comfortable") are mapped at resolve time — no migration needed.

// ── Font family (stays discrete — dropdown, not slider) ───────────────────────
export type FontFamilyOption = "system" | "helvetica" | "georgia" | "times" | "verdana";
export const FONT_FAMILY_OPTIONS: readonly FontFamilyOption[] =
  ["system", "helvetica", "georgia", "times", "verdana"] as const;
export const DEFAULT_FONT_FAMILY: FontFamilyOption = "system";

// The exact Inter stack the embed root + RequestPageClient hardcode today.
// `system` MUST equal this verbatim so default output stays byte-identical.
const SYSTEM_FONT_STACK =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif";

// Curated whitelist only — no arbitrary CSS / font URLs.
export const FONT_STACKS: Record<FontFamilyOption, string> = {
  system: SYSTEM_FONT_STACK,
  helvetica: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  georgia: "Georgia, 'Times New Roman', Times, serif",
  times: "'Times New Roman', Times, Georgia, serif",
  verdana: "Verdana, 'Segoe UI', Geneva, Tahoma, sans-serif",
};
export const FONT_FAMILY_LABELS: Record<FontFamilyOption, string> = {
  system: "System (Inter)",
  helvetica: "Helvetica / Arial",
  georgia: "Georgia (serif)",
  times: "Times New Roman (serif)",
  verdana: "Verdana",
};

// ── Slider ranges + defaults (UI constants for the admin panel) ───────────────
// These define the slider min/max/step/default; the resolver accepts any value
// in range and degrades gracefully for out-of-range inputs.
export const SIZE_SLIDER = { min: 0.85, max: 1.20, step: 0.05, default: 1.0 } as const;
export const BUTTON_RADIUS_SLIDER = { min: 0, max: 20, step: 1, default: 9 } as const;
export const DENSITY_SLIDER = { min: 0.80, max: 1.30, step: 0.05, default: 1.0 } as const;

// ── Back-compat maps for legacy Phase-2c enum strings ────────────────────────
// Phase-2c stored these as string enums. The resolver maps them so stores
// that were saved before this change still render correctly.

const LEGACY_SIZE: Record<string, number> = {
  small: 0.9, medium: 1.0, large: 1.15,
};
// null = use the renderer's own per-renderer default (preserves byte-identical
// output for stores where "rounded" was saved — embed=9, request=10, preview=8).
const LEGACY_RADIUS: Record<string, number | null> = {
  square: 2, rounded: null, pill: 999,
};
const LEGACY_DENSITY: Record<string, number> = {
  compact: 0.85, comfortable: 1.0, spacious: 1.2,
};

// ── Resolver types ────────────────────────────────────────────────────────────
// Accepts ShopConfig, PublicShopConfig, and the embed's inlined ShopConfig
// (all four keys optional). form_size / form_button_radius / form_density
// accept both legacy string enums and new numeric values.
export interface FormDesignTokenInput {
  form_font_family?: string;
  form_size?: string | number;
  form_button_radius?: string | number;
  form_density?: string | number;
}

export interface ResolvedFormDesignTokens {
  /** Concrete font-family stack to apply. */
  fontStack: string;
  /** Uniform scale factor (1 = no scaling). Applied only when !== 1. */
  scale: number;
  /** Button corner radius in px. null = use the renderer's own existing
   *  per-renderer default (embed 9, request 10, preview 8). This preserves
   *  byte-identical output for stores where form_button_radius is unset or
   *  was saved as the legacy "rounded" enum.
   *  Callers: tokens.buttonRadius ?? ownRendererDefault */
  buttonRadius: number | null;
  /** Spacing multiplier (1 = today's spacing). Applied via scaleSpacing(). */
  spacing: number;
  /** Resolved font-family option key. Used for the "only apply when
   *  non-system" guard so default output stays byte-identical. */
  fontFamily: FontFamilyOption;
}

function pickFont(v: string | undefined | null): FontFamilyOption {
  const opts: readonly FontFamilyOption[] = FONT_FAMILY_OPTIONS;
  return v != null && (opts as readonly string[]).includes(v) ? (v as FontFamilyOption) : DEFAULT_FONT_FAMILY;
}

/**
 * Resolve all four design tokens from config with byte-identical defaults.
 *
 * form_size / form_button_radius / form_density: accepts a number (new, from
 * the slider) or a legacy enum string (back-compat from Phase-2c).
 *
 * buttonRadius null contract: null means "use the renderer's own per-renderer
 * default" — callers write `tokens.buttonRadius ?? ownDefault` (9 for the
 * embed, 10 for the request page, 8 for the preview). Returned for "rounded"
 * and undefined/null inputs so those stores stay byte-identical.
 */
export function resolveFormDesignTokens(
  config: FormDesignTokenInput | null | undefined,
): ResolvedFormDesignTokens {
  const fontFamily = pickFont(config?.form_font_family);

  // form_size — numeric scale (new) or legacy enum (back-compat)
  const rawSize = config?.form_size;
  const scale = typeof rawSize === "number"
    ? Math.max(0.5, Math.min(2.0, rawSize))
    : (typeof rawSize === "string" && rawSize in LEGACY_SIZE ? LEGACY_SIZE[rawSize] : 1.0);

  // form_button_radius — px number (new) or legacy string (back-compat).
  // "rounded" / null / undefined → null (each renderer applies its own default).
  const rawRadius = config?.form_button_radius;
  let buttonRadius: number | null;
  if (typeof rawRadius === "number") {
    buttonRadius = Math.max(0, rawRadius);
  } else if (typeof rawRadius === "string" && rawRadius in LEGACY_RADIUS) {
    buttonRadius = LEGACY_RADIUS[rawRadius];
  } else {
    buttonRadius = null;
  }

  // form_density — numeric multiplier (new) or legacy enum (back-compat)
  const rawDensity = config?.form_density;
  const spacing = typeof rawDensity === "number"
    ? Math.max(0.5, Math.min(2.0, rawDensity))
    : (typeof rawDensity === "string" && rawDensity in LEGACY_DENSITY ? LEGACY_DENSITY[rawDensity] : 1.0);

  return { fontStack: FONT_STACKS[fontFamily], scale, buttonRadius, spacing, fontFamily };
}

/**
 * Multiply a base spacing px by the density multiplier and round to an
 * integer. At multiplier 1.0 this returns the base value unchanged, so
 * default spacing stays byte-identical.
 */
export function scaleSpacing(basePx: number, multiplier: number): number {
  return Math.round(basePx * multiplier);
}
